import { useQuery } from '@apollo/client/react';
import { STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS } from '@staff-app/graphql-client';
import { customerReadVars } from '../lib/drawer-mode';
import { AGENT_PRESENCE } from '../components/testing-drawer/lib/connectivity';
import {
  parseDeviceLiveInfo,
  type DeviceLiveInfo,
} from '../components/testing-drawer/lib/parse-device-info';

interface Result {
  staffProxyTestingDeviceMdmDetails: string | null;
}

export type MdmLiveInfoStatus = 'skipped' | 'loading' | 'live' | 'no-response';

/**
 * Pull the device's CURRENT state when the drawer opens — `mdmDetails(GET_DEVICE_INFO)`, the device's
 * own request→ack reply. Port of scio3 `useMdmLiveInfo`. Read-only: it asks the device to report,
 * never changes it.
 *
 * Gating mirrors the server, so we never fire a request we know comes back empty:
 *  - utils-mdm   → the agent socket must be up (`utilsOnline === 1`)
 *  - builtin-mdm → the request rides the execution rail to the player app, so the app must be online
 *  - anything else (none / platform-api / unknown) → skipped; the caller shows snapshot values
 */
export function useTestingMdmLiveInfo(input: {
  deviceId: string | null | undefined;
  managementCapability: string | null | undefined;
  utilsOnline: number | null | undefined;
  appOnline: boolean;
  /** Customer drawer only — routes the read through staff-api's customer gate. */
  accountId?: string;
}): { info: DeviceLiveInfo | null; status: MdmLiveInfoStatus; refetch: () => void } {
  const { deviceId, managementCapability: cap, utilsOnline, appOnline, accountId } = input;

  let onlineOk = false;
  if (cap === 'utils-mdm') onlineOk = utilsOnline === AGENT_PRESENCE.ONLINE;
  else if (cap === 'builtin-mdm') onlineOk = appOnline;
  const skip = !deviceId || !onlineOk;

  const { data, loading, refetch } = useQuery<Result>(STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS, {
    variables: { deviceId: deviceId ?? '', command: 'GET_DEVICE_INFO', ...customerReadVars(accountId) },
    skip,
    fetchPolicy: 'network-only',
  });

  const info = skip ? null : parseDeviceLiveInfo(data?.staffProxyTestingDeviceMdmDetails);

  let status: MdmLiveInfoStatus;
  if (skip) status = 'skipped';
  else if (loading && !info) status = 'loading';
  else if (info) status = 'live';
  else status = 'no-response';

  // An explicit refetch wants the device's REAL state — bypass the server's 15s cache.
  return { info, status, refetch: () => void refetch({ fresh: true }) };
}
